import { z } from 'zod';

import { addressSchema } from './address';
import { currencySchema } from './currency';
import {
  emailSchema,
  isoDateTimeSchema,
  minorUnitsBigintSchema,
  phoneSchema,
  slugSchema,
  ulidSchema,
  userIdSchema,
} from './primitives';

export const orderStateSchema = z.enum([
  'AWAITING_PAYMENT',
  'AWAITING_RESTAURANT_CONFIRMATION',
  'CONFIRMED',
  'PREPARING',
  'READY',
  'OUT_FOR_DELIVERY',
  'COMPLETED',
  'HELD_FOR_DISPUTES',
  'DISPUTED',
  'PENDING_PAYOUT',
  'PARTIALLY_REFUNDED',
  'REFUNDED',
  'PAID_OUT',
  'CANCELLED',
]);
export type OrderState = z.infer<typeof orderStateSchema>;

export const payoutStatusSchema = z.enum([
  'NONE',
  'PENDING_PAYOUT',
  'PAID_OUT',
  'EXCLUDED_FROM_PAYOUT',
]);
export type PayoutStatus = z.infer<typeof payoutStatusSchema>;

export const fulfillmentSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('PICKUP') }),
  z.object({ type: z.literal('RESTAURANT_DELIVERY'), deliveryAddress: addressSchema }),
  z.object({
    type: z.literal('PLATFORM_DELIVERY'),
    deliveryAddress: addressSchema,
    assignedDriverId: ulidSchema.optional(),
  }),
]);
export type Fulfillment = z.infer<typeof fulfillmentSchema>;

export const deliveryFeeRecipientSchema = z.enum(['restaurant', 'platform']);
export type DeliveryFeeRecipient = z.infer<typeof deliveryFeeRecipientSchema>;

export const orderLineItemSchema = z.object({
  menuItemId: ulidSchema,
  name: z.string(),
  quantity: z.number().int().min(1),
  unitPriceMinorUnits: minorUnitsBigintSchema,
  lineTotalMinorUnits: minorUnitsBigintSchema,
});
export type OrderLineItem = z.infer<typeof orderLineItemSchema>;

export const requestedDeliveryTimeSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('asap') }),
  z.object({ mode: z.literal('scheduled'), at: isoDateTimeSchema }),
]);
export type RequestedDeliveryTime = z.infer<typeof requestedDeliveryTimeSchema>;

export const disputeStatusSchema = z.enum(['OPEN']).optional();
export const disputeStageSchema = z.enum(['AWAITING_RESTAURANT', 'AWAITING_ADMIN']).optional();
export const disputeResolutionSchema = z
  .enum(['PAYOUT', 'FULL_REFUND', 'PARTIAL_REFUND'])
  .optional();

export const disputeResolvedBySchema = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('RESTAURANT'), userId: userIdSchema }),
    z.object({ kind: z.literal('ADMIN'), userId: userIdSchema }),
  ])
  .optional();
export type DisputeResolvedBy = z.infer<typeof disputeResolvedBySchema>;

export const orderEntitySchema = z.object({
  orderId: ulidSchema,
  restaurantId: ulidSchema,
  restaurantSlug: slugSchema.optional(),
  customerUserId: userIdSchema.optional(),
  guestEmail: emailSchema.optional(),
  guestPhone: phoneSchema.optional(),

  state: orderStateSchema,

  paymentProviderId: z.string().min(1),
  paymentIntentId: z.string().optional(),
  paymentStatus: z
    .enum(['requires_action', 'pending', 'succeeded', 'failed', 'refunded'])
    .optional(),
  payoutStatus: payoutStatusSchema.default('NONE'),

  currency: currencySchema,
  subtotalMinorUnits: minorUnitsBigintSchema,
  deliveryFeeMinorUnits: minorUnitsBigintSchema,
  deliveryFeeRecipient: deliveryFeeRecipientSchema,
  totalMinorUnits: minorUnitsBigintSchema,

  serviceFeeSnapshotBasisPoints: minorUnitsBigintSchema.optional(),
  restaurantPayoutAmountMinorUnits: minorUnitsBigintSchema.optional(),

  refundedFromSubtotalMinorUnits: minorUnitsBigintSchema.default(0n),
  refundedFromDeliveryFeeMinorUnits: minorUnitsBigintSchema.default(0n),
  refundedFromPlatformGoodwillMinorUnits: minorUnitsBigintSchema.default(0n),

  fulfillment: fulfillmentSchema,
  requestedDeliveryTime: requestedDeliveryTimeSchema,

  lineItems: z.array(orderLineItemSchema),

  notes: z.string().max(2000).optional(),

  voucherId: ulidSchema.optional(),
  voucherCodeNormalized: z.string().optional(),
  voucherDiscountMinorUnits: minorUnitsBigintSchema.optional(),
  voucherFromRestaurantPayoutMinorUnits: minorUnitsBigintSchema.optional(),
  voucherFromServiceFeeMinorUnits: minorUnitsBigintSchema.optional(),

  enteredHeldAt: isoDateTimeSchema.optional(),
  disputeStatus: disputeStatusSchema,
  disputeStage: disputeStageSchema,
  disputedAt: isoDateTimeSchema.optional(),
  disputeReason: z.string().optional(),
  disputeDetails: z.string().optional(),
  restaurantDeclinedAt: isoDateTimeSchema.optional(),
  restaurantDeclineNote: z.string().optional(),
  restaurantProposedRefundFromSubtotalMinorUnits: minorUnitsBigintSchema.optional(),
  restaurantProposedRefundFromDeliveryFeeMinorUnits: minorUnitsBigintSchema.optional(),
  disputeResolution: disputeResolutionSchema,
  disputeResolvedAt: isoDateTimeSchema.optional(),
  disputeResolvedBy: disputeResolvedBySchema,
  disputeResolverNote: z.string().optional(),

  reviewRequestEmailSentAt: isoDateTimeSchema.optional(),

  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  completedAt: isoDateTimeSchema.optional(),
});
export type OrderEntity = z.infer<typeof orderEntitySchema>;

/** Customer / public projection — what the order's owner (customer) sees. */
export const orderCustomerSchema = z.object({
  orderId: ulidSchema,
  restaurantId: ulidSchema,
  restaurantSlug: slugSchema.optional(),
  state: orderStateSchema,
  currency: currencySchema,
  subtotalMinorUnits: z.string(),
  deliveryFeeMinorUnits: z.string(),
  totalMinorUnits: z.string(),
  fulfillment: fulfillmentSchema,
  requestedDeliveryTime: requestedDeliveryTimeSchema,
  lineItems: z.array(
    orderLineItemSchema.extend({
      unitPriceMinorUnits: z.string(),
      lineTotalMinorUnits: z.string(),
    }),
  ),
  notes: z.string().optional(),
  totalRefundedMinorUnits: z.string(),
  disputeStage: disputeStageSchema,
  disputeResolution: disputeResolutionSchema,
  disputeResolvedAt: isoDateTimeSchema.optional(),
  disputeResolvedByKind: z.enum(['RESTAURANT', 'ADMIN']).optional(),
  disputeReason: z.string().optional(),
  disputeDetails: z.string().optional(),
  createdAt: isoDateTimeSchema,
  completedAt: isoDateTimeSchema.optional(),
});
export type OrderCustomer = z.infer<typeof orderCustomerSchema>;

/** Owner projection — restaurant members. Adds dispute breakdown but never admin user ids. */
export const orderOwnerSchema = orderCustomerSchema.extend({
  customerUserId: userIdSchema.optional(),
  guestEmail: emailSchema.optional(),
  guestPhone: phoneSchema.optional(),
  payoutStatus: payoutStatusSchema,
  disputeStatus: disputeStatusSchema,
  refundedFromSubtotalMinorUnits: z.string(),
  refundedFromDeliveryFeeMinorUnits: z.string(),
  /** Only present when the resolver was a member of THIS restaurant. */
  disputeResolvedByMemberUserId: ulidSchema.optional(),
  disputeResolverNote: z.string().optional(),
  restaurantDeclineNote: z.string().optional(),
  restaurantProposedRefundFromSubtotalMinorUnits: z.string().optional(),
  restaurantProposedRefundFromDeliveryFeeMinorUnits: z.string().optional(),
});
export type OrderOwner = z.infer<typeof orderOwnerSchema>;

/** Admin projection — full record, including admin user ids and refund-bucket breakdown. */
export const orderAdminSchema = orderOwnerSchema.extend({
  paymentProviderId: z.string(),
  paymentIntentId: z.string().optional(),
  paymentStatus: z.string().optional(),
  refundedFromPlatformGoodwillMinorUnits: z.string(),
  disputeResolvedBy: disputeResolvedBySchema,
  enteredHeldAt: isoDateTimeSchema.optional(),
  disputedAt: isoDateTimeSchema.optional(),
  restaurantDeclinedAt: isoDateTimeSchema.optional(),
  reviewRequestEmailSentAt: isoDateTimeSchema.optional(),
  updatedAt: isoDateTimeSchema,
  restaurantContactEmail: emailSchema.optional(),
  restaurantContactPhone: phoneSchema.optional(),
  customerContactEmail: emailSchema.optional(),
  customerContactPhone: phoneSchema.optional(),
});
export type OrderAdmin = z.infer<typeof orderAdminSchema>;
